import z from "zod";

import { type Env } from "../http/Env";
import { type LoggerInterface } from "../logger-types";
import { type Cell, type Page, page } from "../prosemirror-serialization";
import { graphqlRequest } from "./graphqlRequest";

export interface QueryPageByIdRequestOptions {
	pageId: string;
	env: Env;
	fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
	authorizationHeader: string | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	cellFn: (cell: any, logger?: LoggerInterface) => Cell;
}

export async function queryPageById({
	pageId,
	env,
	fetch,
	authorizationHeader,
	cellFn,
}: QueryPageByIdRequestOptions): Promise<Page> {
	const response = await graphqlRequest({
		env,
		fetch,
		authorizationHeader,
		query: queryPageByIdOperation(pageId),
		responseSchema: queryPageByIdRawResponseSchema,
	});
	// Parse the cell information appropriately
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const responseAsAny = response.pageByID as any;
	let cellArray = [];
	if ("cells" in responseAsAny && Array.isArray(responseAsAny["cells"])) {
		cellArray = responseAsAny.cells.map((cell) => cellFn(cell));
	}
	const result = {
		...responseAsAny,
		cells: cellArray,
	};
	const parse = queryPageByIdResponseSchema.parse({
		pageByID: result,
	});
	return parse.pageByID;
}

const queryPageByIdRawResponseSchema = z.object({ pageByID: z.unknown() });
const queryPageByIdResponseSchema = z.object({ pageByID: page });
export type QueryPageByIdResponse = z.infer<typeof queryPageByIdResponseSchema>;

const queryPageByIdOperation = (pageID: string) => ({
	operationName: "PageByID",
	query: `
	query PageByID($pageID: ID!) {
		pageByID(pageID: $pageID) {
			id
			title
			canvasID
			authorID
			slug
			latestVersion
			parentPageID
			cells {
				type
				cellName
				code
				id
				nodeAttrs
				__typename
			}
		  __typename
		}
	}`,
	variables: { pageID },
});
